/**
 * Request.navigationHandoff.ts
 *
 * Handles consuming RequestAccessHandoffPayload (written by Request History Edit/Renew actions)
 * and translating it into the data format that each tenant's prePopulate* method expects.
 *
 * Legacy reference: MyAccess.tsx onEditBtnClick (line ~1141) + _closeModal (line ~861)
 *   - Mercury / Alfred / MDS: passes `item` directly (from getApprovedItems result)
 *   - MSSales: passes approvedAccess filtered by DefaultReportingSubsidiary presence
 *
 * Each prePopulate* in Request.tsx expects a specific shape:
 *   prePopulateMercuryAccess(data)  — passes item directly to MercuryRequest, no fixed shape required
 *   prePopulateAlfredAccess(data)   — needs: { roleId, role, multiAttributes[0].attributeName, multiAttributes[0].attributeValueText }
 *   prePopulateMDSAccess(data)      — needs: { roleId, role, roleDescription, multiAttributes: [{ attributeName, attributeValueText }] }
 *   prePopulateMSSalesAccess(data)  — handled separately via tryApplyMSSalesNavigationHandoff (has its own ApprovedAccess matching)
 *
 * multiAttributes format (matches MyAccess.tsx processAttributesFromObject output for non-Mercury):
 *   attributeValueText: "code:description; code:description; ..."
 *   attributeName: the attribute key (e.g., "FiscalCycles", "Region", "Country")
 */

import AccessAPI from '../../store/AccessAPI';
import { RequestAccessHandoffPayload } from './MyAccessRequests.types';

// Attributes that belong to MSSales-specific fields — skip when building multiAttributes for Alfred/MDS
const SYSTEM_ATTRIBUTES = ['UserAlias', 'DefaultReportingSubsidiary', 'Subsidiary', 'Business'];

/**
 * Builds the multiAttributes array from a raw approvedAccess item's attributes object.
 *
 * Mirrors MyAccess.tsx processAttributesFromObject for non-Mercury tenants:
 * - Iterates over each attribute key
 * - Joins array values with '; ' separator
 * - Skips system/MSSales-specific attributes
 *
 * Result is compatible with prePopulateAlfredAccess and prePopulateMDSAccess expectations.
 */
export function buildMultiAttributesFromRaw(
    attributes: Record<string, any>
): { attributeName: string; attributeValueText: string }[] {
    if (!attributes) return [];

    const result: { attributeName: string; attributeValueText: string }[] = [];

    Object.keys(attributes).forEach(key => {
        if (SYSTEM_ATTRIBUTES.includes(key)) return;

        const values = attributes[key];
        if (!values || (Array.isArray(values) && values.length === 0)) return;

        const attributeValueText = Array.isArray(values)
            ? values.map(v => String(v)).join('; ')
            : String(values);

        result.push({ attributeName: key, attributeValueText });
    });

    return result;
}

/**
 * Finds the approved access entry matching the handoff item and builds multiAttributes.
 *
 * Accepts optional pre-loaded approved access data to avoid an extra API call.
 * - MDS: pass this.state.myAccess.approvedAccess (loaded during onInit in Request.tsx)
 * - Alfred: no pre-loaded data available in Request.tsx, so an API call is made
 *
 * Returns null if no match or request fails.
 */
export async function fetchApprovedAccessForHandoff(
    tenantId: string,
    handoff: RequestAccessHandoffPayload,
    preLoadedApproved?: any[]
): Promise<{ roleId: string; role: string; roleDescription: string; multiAttributes: { attributeName: string; attributeValueText: string }[] } | null> {
    try {
        let approved: any[];

        if (preLoadedApproved && preLoadedApproved.length > 0) {
            approved = preLoadedApproved;
        } else {
            const res = await AccessAPI.getMyAccess(tenantId);
            approved = res?.Data?.approvedAccess || [];
        }

        const sameRole = approved.filter(x =>
            x.roleDefinitionId === handoff.item.roleId
            || x.roleName?.toLowerCase() === handoff.item.role?.toLowerCase()
        );

        const handoffEndDateDay = handoff.item.originalEndDate
            ? new Date(handoff.item.originalEndDate).toISOString().split('T')[0]
            : null;

        const match = (handoffEndDateDay
            ? sameRole.find(x => {
                const accessEnd = x.endDate ? new Date(x.endDate).toISOString().split('T')[0] : null;
                return accessEnd === handoffEndDateDay;
            })
            : undefined) ?? sameRole[0];

        if (!match) return null;

        const multiAttributes = buildMultiAttributesFromRaw(match.attributes ?? {});

        // Alfred requires at least one multiAttribute entry (FiscalCycles) for prePopulateAlfredAccess.
        // If empty, the approved access has no attribute data to pre-populate — skip handoff gracefully.
        if (multiAttributes.length === 0) return null;

        return {
            roleId: match.roleDefinitionId ?? handoff.item.roleId,
            role: match.roleName ?? handoff.item.role,
            roleDescription: match.roleDescription ?? '',
            multiAttributes,
        };
    } catch {
        return null;
    }
}
